import React from 'react'
import { useApp } from '../context/AppContext'
import { ApiError, api } from '../api/client'
import type { LegalDocument, LegalText } from '../api/client'
import { agreementGroups, agreementsSentence, inApp } from '../privacy/agreements'
import { saveErrorMessage } from './settings/toast'
import './Auth.css'
import AuthLayout, { AuthLogo } from './AuthLayout'

// Agreements stands before the app when an agreement in force (the terms, the
// DPA, the DPIA) has not been accepted at its current version. She reads the
// terms and the DPA at their published URLs, and the DPIA here, in the app,
// which is the only place it is served (owner, 2026-10-29); then she agrees,
// and the API records which version, and when. The contract has one tickbox
// and the DPIA another, since she signs the one for herself and adopts the
// other for her clients. Her ticks belong to the documents she saw, so a
// different list is a new form with nothing ticked.
export default function Agreements({ pending }: { pending: LegalDocument[] }) {
  const listed = pending.map((d) => `${d.document}@${d.version}`).join()
  return <AgreementsForm key={listed} pending={pending} />
}

function AgreementsForm({ pending }: { pending: LegalDocument[] }) {
  const { dispatch } = useApp()
  const { contract, dpia } = agreementGroups(pending)
  const [agreedContract, setAgreedContract] = React.useState(false)
  const [agreedDpia, setAgreedDpia] = React.useState(false)
  const [saving, setSaving] = React.useState(false)
  const [error, setError] = React.useState('')
  // The in-app document she is reading, if any; her ticks wait underneath.
  const [reading, setReading] = React.useState<LegalDocument | null>(null)
  const ready = pending.length > 0 && (contract.length === 0 || agreedContract) && (dpia.length === 0 || agreedDpia)

  const accept = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true)
    setError('')
    try {
      await api.acceptAgreements(pending)
    } catch (err) {
      // A newer version went live while this screen was open: show it.
      if (!(err instanceof ApiError && err.status === 409)) {
        setError(saveErrorMessage(err))
        setSaving(false)
        return
      }
    }
    try {
      dispatch({ type: 'SESSION_STARTED', payload: await api.me() })
    } catch (err) {
      setError(saveErrorMessage(err))
    }
    setAgreedContract(false)
    setAgreedDpia(false)
    setSaving(false)
  }

  if (reading) {
    return (
      <AuthLayout>
        <LegalReader doc={reading} onBack={() => setReading(null)} />
      </AuthLayout>
    )
  }

  return (
    <AuthLayout>
        <form className="auth-form" onSubmit={accept}>
          <AuthLogo />
          <h1>Before you carry on</h1>
          <p className="auth-subtitle">
            Please read {agreementsSentence(pending)}. They cover how Harmovi looks after your business and your
            clients’ information.
          </p>
          <ul className="agreements-list">
            {pending.map((d) => (
              <li key={d.document}>
                {inApp(d) ? (
                  <a href={d.url} onClick={(e) => { e.preventDefault(); setReading(d) }}>
                    {d.title}
                  </a>
                ) : (
                  <a href={d.url} target="_blank" rel="noopener noreferrer">
                    {d.title}
                  </a>
                )}
              </li>
            ))}
          </ul>
          {contract.length > 0 && (
            <label className="agreements-check">
              <input type="checkbox" checked={agreedContract} onChange={(e) => setAgreedContract(e.target.checked)} />
              <span>I have read and agree to {agreementsSentence(contract)}.</span>
            </label>
          )}
          {dpia.length > 0 && (
            <label className="agreements-check">
              <input type="checkbox" checked={agreedDpia} onChange={(e) => setAgreedDpia(e.target.checked)} />
              <span>I have read the data protection impact assessment and adopt it for my clients.</span>
            </label>
          )}
          {error && <p className="field-error" role="alert">{error}</p>}
          <button type="submit" className="btn btn-primary" disabled={!ready || saving}>
            {saving ? 'One moment…' : 'Agree and continue'}
          </button>
        </form>
    </AuthLayout>
  )
}

// LegalReader shows a document the app serves itself, in the card, with the
// way back to the form above it. The HTML is Harmovi's own, built from the
// draft at publish time (site/build-legal.mjs), never anything she or a
// client typed.
function LegalReader({ doc, onBack }: { doc: LegalDocument; onBack: () => void }) {
  const [text, setText] = React.useState<LegalText | null>(null)
  const [error, setError] = React.useState('')

  React.useEffect(() => {
    let gone = false
    api.legalDocument(doc.url).then(
      (t) => { if (!gone) setText(t) },
      (err) => { if (!gone) setError(saveErrorMessage(err)) },
    )
    return () => { gone = true }
  }, [doc.url])

  return (
    <article className="auth-form legal-reader" aria-labelledby="legal-reader-title">
      <button type="button" className="btn btn-quiet legal-reader-back" onClick={onBack}>
        ‹ Back to the agreements
      </button>
      <h1 id="legal-reader-title">{doc.title}</h1>
      {error && <p className="field-error" role="alert">{error}</p>}
      {!text && !error && <p className="auth-subtitle">Loading…</p>}
      {text && <div className="legal-doc" dangerouslySetInnerHTML={{ __html: text.html }} />}
      <button type="button" className="btn btn-primary" onClick={onBack}>
        Back to the agreements
      </button>
    </article>
  )
}
